'use client'

import { motion } from 'framer-motion'
import { Search, PenTool, Code2, Rocket, LifeBuoy, GitBranch } from 'lucide-react'

const steps = [
  { icon: Search, title: 'Discovery', desc: 'We dig into your goals, users, and constraints to define a clear scope.', color: 'from-sky-400 to-blue-500' },
  { icon: PenTool, title: 'Design', desc: 'Wireframes and polished UI prototypes you can click through before we build.', color: 'from-violet-400 to-purple-500' },
  { icon: Code2, title: 'Development', desc: 'Agile sprints with clean, tested, reviewable code and regular demos.', color: 'from-emerald-400 to-teal-500' },
  { icon: Rocket, title: 'Launch', desc: 'Automated CI/CD, performance tuning, and a smooth production rollout.', color: 'from-orange-400 to-red-500' },
  { icon: LifeBuoy, title: 'Support', desc: 'Monitoring, iteration, and 24/7 support to keep you growing.', color: 'from-pink-400 to-rose-500' },
]

export default function Process() {
  return (
    <section className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-sm font-medium mb-4">
            <GitBranch className="w-4 h-4" /> How We Work
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Our Proven <span className="gradient-text">Process</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
            A transparent, repeatable path from idea to impact
          </p>
        </motion.div>

        <div className="relative">
          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-10 left-0 right-0 h-0.5 bg-gradient-to-r from-sky-200 via-blue-200 to-pink-200" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-4 relative">
            {steps.map((s, i) => {
              const Icon = s.icon
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="relative text-center"
                >
                  <div className="flex justify-center mb-5">
                    <div className={`relative w-20 h-20 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white shadow-lg`}>
                      <Icon className="w-8 h-8" />
                      <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white text-slate-900 text-sm font-bold flex items-center justify-center shadow">
                        {i + 1}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed px-2">{s.desc}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
